import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router, UrlTree } from '@angular/router';
import { map, catchError, Observable, of } from 'rxjs';
import { ROUTE_CONSTANTS } from '../constants/route.constant';
import { AuthApiService } from '@app/modules/auth/services/auth-api/auth-api.service';

/**
 * Guard to protect routes requiring authentication.
 *
 * This guard checks if the user is currently authenticated by calling the backend
 * via `getMe()` endpoint, which uses `accessToken` from cookies.
 *
 * If the token is valid, navigation is allowed.
 * If the request fails (e.g., token expired or unauthorized), the user is redirected
 * to the login page and navigation is denied.
 *
 * Supports both route and child route guarding via `CanActivate` and `CanActivateChild`.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
    constructor(
        private readonly authService: AuthApiService,
        private readonly router: Router
    ) {}

    /**
     * Checks whether the user can access a protected route.
     */
    canActivate(): Observable<boolean | UrlTree> {
        return this.checkAccess();
    }

    /**
     * Checks whether the user can access a protected child route.
     */
    canActivateChild(): Observable<boolean | UrlTree> {
        return this.checkAccess();
    }

    /**
     * Common logic to verify access based on backend authentication check.
     * Redirects to login if not authenticated.
     */
    private checkAccess(): Observable<boolean | UrlTree> {
        return this.authService.user$.pipe(
            map((user) => {
                return user ? true : this.router.createUrlTree([ROUTE_CONSTANTS.AUTH.ROOT, ROUTE_CONSTANTS.AUTH.LOGIN]);
            }),
            catchError(() => {
                // User is not authenticated or token is invalid
                return of(this.router.createUrlTree([ROUTE_CONSTANTS.AUTH.ROOT, ROUTE_CONSTANTS.AUTH.LOGIN]));
            })
        );
    }
}
